import { useCallback, useEffect, useRef, useState } from "react"
import { useCookies } from "react-cookie"
import { Outlet, useLocation, useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { CircleAlertIcon } from "lucide-react"

import { Button, SidebarProvider } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { AuthGuard } from "@/components/Auth"
import { ErrorPage } from "@/components/System"
import DataLoader from "@/components/DataLoader"
import { Copilot } from "@/components"
import { clampCopilotDrawerWidth } from "@/components/Copilot/drawerWidth"
import { useSysConfig, useWindowSize } from "@/hooks"
import { useStore } from "@/store"
import { Organization } from "@/types"
import {
  FetchStatus,
  useGetOrganizationsApi,
  organizationResponseToOrganization,
  useUpdateProfileApi,
  useGetBillingSubscriptionApi,
  transformBillingOverview,
} from "@/hooks/api"
import type { BillingOverview } from "@/hooks/api"
import { useToast } from "@/hooks"
import { BILLING_OVERVIEW_REFRESH_EVENT, SETTINGS_ENDPOINTS } from "@/constants"

import AppSidebar from "./AppSidebar"
import MobileAppHeader from "./MobileAppHeader"

const AppLayout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { width: viewportWidth } = useWindowSize()
  const [cookies] = useCookies(["sidebar:state"])
  const [
    userMe,
    currentOrganization,
    currentHistory,
    copilotMode,
    isCopilotOpen,
    copilotDrawerWidth,
    setCurrentHistory,
    setOrganizations,
    setCurrentOrganization,
    updateUserMeProfile,
  ] = useStore((state) => [
    state.userMe,
    state.currentOrganization,
    state.currentHistory,
    state.copilotMode,
    state.isCopilotOpen,
    state.copilotDrawerWidth,
    state.setCurrentHistory,
    state.setOrganizations,
    state.setCurrentOrganization,
    state.updateUserMeProfile,
  ])

  const { alertToast, successToast } = useToast()
  const { t } = useTranslation()
  const { billingUiEnabled } = useSysConfig()
  const isBillingUiEnabled = billingUiEnabled ?? true
  const currentOrgIdRef = useRef<string>("")
  const billingOverviewOwnerIdRef = useRef<string>("")
  const billingOverviewRequestIdRef = useRef(0)
  const billingOverviewSuccessRequestIdRef = useRef(0)
  const [noOrganization, setNoOrganization] = useState(false)
  const [isSwitchingOrg, setIsSwitchingOrg] = useState(false)
  const [billingOverviewState, setBillingOverviewState] = useState<{
    organizationId: string
    overview: BillingOverview
  } | null>(null)
  const effectiveCopilotDrawerWidth = clampCopilotDrawerWidth({
    desiredWidth: copilotDrawerWidth,
    pathname: location.pathname,
    viewportWidth,
  })
  const isStandaloneHistoryRoute =
    /^\/workspaces\/[^/]+\/histories\/[^/]+$/.test(location.pathname)
  const workspaceIdFromPath = location.pathname.match(
    /^\/workspaces\/([^/]+)\/histories\/[^/]+$/
  )?.[1]
  const historyIdFromPath = location.pathname.match(
    /^\/workspaces\/[^/]+\/histories\/([^/]+)$/
  )?.[1]
  const currentRouteHistory =
    currentHistory && String(currentHistory.id) === historyIdFromPath
      ? currentHistory
      : null
  const historyListHref =
    isStandaloneHistoryRoute && workspaceIdFromPath
      ? `/workspaces/${currentRouteHistory?.workspace?.id ?? workspaceIdFromPath}?tab=conversations`
      : null

  // API
  const {
    status: organizationsStatus,
    error: organizationsError,
    data: organizationsResp,
    fetchFn: getOrganizationsApi,
  } = useGetOrganizationsApi()

  const { fetchFn: updateProfile } = useUpdateProfileApi()
  const { fetchFn: fetchBillingOverview } = useGetBillingSubscriptionApi()

  const isCreditLocked =
    billingOverviewState?.organizationId === currentOrganization?.id &&
    Boolean(billingOverviewState?.overview.lockedAt)
  const isOrganizationPlanPage =
    location.pathname === SETTINGS_ENDPOINTS.organizationPlan

  const refreshBillingOverview = useCallback(
    (options?: { resetOwner?: boolean }) => {
      if (!currentOrganization?.id) return Promise.resolve()
      const organizationId = currentOrganization.id
      const requestId = billingOverviewRequestIdRef.current + 1
      billingOverviewRequestIdRef.current = requestId
      if (options?.resetOwner) {
        billingOverviewSuccessRequestIdRef.current = requestId - 1
        setBillingOverviewState(null)
      }

      return fetchBillingOverview({
        searchParams: new URLSearchParams({
          organization_id: organizationId,
        }),
        preserveData: true,
      })
        .then((resp) => {
          if (!resp?.data) return
          if (organizationId !== billingOverviewOwnerIdRef.current) return
          if (requestId <= billingOverviewSuccessRequestIdRef.current) return
          billingOverviewSuccessRequestIdRef.current = requestId
          setBillingOverviewState({
            organizationId,
            overview: transformBillingOverview(resp.data),
          })
        })
        .catch((error) => {
          if (requestId !== billingOverviewRequestIdRef.current) return
          console.warn("Failed to load billing overview.", error)
        })
    },
    [currentOrganization?.id, fetchBillingOverview]
  )

  useEffect(() => {
    billingOverviewOwnerIdRef.current = currentOrganization?.id ?? ""
  }, [currentOrganization?.id])

  // Auto-switch organization based on workspace in URL
  useEffect(() => {
    if (!userMe?.profile) return
    if (organizationsStatus === FetchStatus.Loading) return
    if (isSwitchingOrg) return

    const handleOrgSwitch = async () => {
      // Extract workspaceId from URL (e.g., /workspaces/:workspaceId/...)
      const pathMatch = location.pathname.match(/\/workspaces\/([^/]+)/)
      const workspaceIdFromUrl = pathMatch?.[1]

      let targetOrgId = userMe.profile.defaultOrganizationId

      // If URL contains a workspace, check if we need to switch organizations
      if (workspaceIdFromUrl && userMe.profile.workspaceOrganizationMap) {
        const workspaceOrgId =
          userMe.profile.workspaceOrganizationMap[workspaceIdFromUrl]

        // If workspace belongs to a different organization, switch to it
        if (
          workspaceOrgId &&
          workspaceOrgId !== userMe.profile.defaultOrganizationId
        ) {
          setIsSwitchingOrg(true)
          try {
            // Wait for profile update to complete
            await updateProfile({
              jsonBody: { default_organization_id: workspaceOrgId },
            })
            targetOrgId = workspaceOrgId

            // Update the store to reflect the new default organization
            // This prevents the effect from running again with the same workspace
            updateUserMeProfile({
              ...userMe.profile,
              defaultOrganizationId: workspaceOrgId,
            })

            successToast({
              title: t("common.orgSwitch.success.title"),
              description: t("common.orgSwitch.success.description"),
            })
          } catch (error) {
            console.error("Failed to switch organization:", error)
            alertToast({
              title: t("common.orgSwitch.error.title"),
              description: t("common.orgSwitch.error.description"),
            })
            // Fall back to default organization
            targetOrgId = userMe.profile.defaultOrganizationId
          } finally {
            setIsSwitchingOrg(false)
          }
        }
      }

      if (!targetOrgId) {
        console.error("No default organization id.", userMe)
        setNoOrganization(true)
        return
      }

      // Only fetch organizations if this is the first time or if we switched to a different org
      if (
        organizationsStatus === FetchStatus.Idle ||
        targetOrgId !== currentOrgIdRef.current
      ) {
        getOrganizationsApi()
      }
      currentOrgIdRef.current = targetOrgId
    }

    handleOrgSwitch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMe, organizationsStatus, location.pathname])

  // Handle organizations data
  useEffect(() => {
    if (!organizationsResp || !organizationsResp.data) return

    let currOrg: Organization | null = null
    const organizations = organizationsResp.data.map((org) => {
      const organization = organizationResponseToOrganization(org)
      if (organization.id === currentOrgIdRef.current) {
        currOrg = organization
      }
      return organization
    })
    setOrganizations(organizations)
    setCurrentOrganization(currOrg)
  }, [organizationsResp, setCurrentOrganization, setOrganizations])

  useEffect(() => {
    if (!isStandaloneHistoryRoute) {
      setCurrentHistory(null)
    }
  }, [isStandaloneHistoryRoute, setCurrentHistory])

  useEffect(() => {
    void refreshBillingOverview({ resetOwner: true })
  }, [refreshBillingOverview])

  useEffect(() => {
    const handleRefresh = () => {
      void refreshBillingOverview()
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") handleRefresh()
    }

    window.addEventListener("focus", handleRefresh)
    window.addEventListener(BILLING_OVERVIEW_REFRESH_EVENT, handleRefresh)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      window.removeEventListener("focus", handleRefresh)
      window.removeEventListener(BILLING_OVERVIEW_REFRESH_EVENT, handleRefresh)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [refreshBillingOverview])

  // need auth first until pass the auth guard and get the user data
  if (!userMe) {
    return <AuthGuard />
  }

  if (organizationsError) {
    return (
      <ErrorPage
        message={`Failed to load the user data, please refresh the page to try again. Error message: ${organizationsResp?.message}`}
      />
    )
  }

  if (noOrganization) {
    return (
      <ErrorPage message="No organization found, please contact the administrator to create an organization." />
    )
  }

  if (!currentOrganization) {
    // not show the loading page to prevent the flickering
    return null
  }

  return (
    <SidebarProvider defaultOpen={cookies["sidebar:state"] === true}>
      <AppSidebar
        user={userMe}
        organization={currentOrganization}
        billingOverview={
          isBillingUiEnabled &&
          billingOverviewState?.organizationId === currentOrganization.id
            ? billingOverviewState.overview
            : null
        }
      />
      <div
        // Constrain layout height so <main> becomes the actual scroll container.
        // This is required for features that rely on the main scroll container
        // (e.g. useScrollToBottom selector: [data-scroll-container="main"]).
        className="isolate h-screen min-w-0 flex-1 flex flex-col min-h-0 transition-[margin-right] duration-200 ease-in-out"
        style={{
          marginRight:
            copilotMode === "drawer" && isCopilotOpen
              ? `${effectiveCopilotDrawerWidth}px`
              : undefined,
        }}
      >
        <MobileAppHeader
          history={isStandaloneHistoryRoute ? currentRouteHistory : null}
          historyListHref={historyListHref}
        />
        <div className="flex flex-1 min-h-0 min-w-0">
          <main
            className={cn(
              "relative mt-3 w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto",
              isStandaloneHistoryRoute ? "md:mt-0" : "md:mt-6"
            )}
            data-scroll-container="main"
          >
            <div className="mx-auto flex h-full min-h-0 w-full min-w-0 max-w-(--breakpoint-2xl) flex-col px-[10px] md:px-[24px] xl:px-[32px]">
              {isCreditLocked && (
                <div
                  role="alert"
                  className="mb-4 flex flex-col gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 gap-3">
                    <CircleAlertIcon
                      className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">
                        {t("billing.creditLockBanner.title")}
                      </div>
                      <div className="mt-1 text-sm text-amber-900">
                        {t("billing.creditLockBanner.description")}
                      </div>
                    </div>
                  </div>
                  {!isOrganizationPlanPage && (
                    <Button
                      type="button"
                      size="sm"
                      className="shrink-0"
                      onClick={() =>
                        navigate(SETTINGS_ENDPOINTS.organizationPlan)
                      }
                    >
                      {t("billing.creditLockBanner.action")}
                    </Button>
                  )}
                </div>
              )}
              <DataLoader organization={currentOrganization}>
                <Outlet />
              </DataLoader>
            </div>
          </main>
        </div>
      </div>

      {/* New Copilot UI */}
      <Copilot />
    </SidebarProvider>
  )
}

export default AppLayout
